import { memo, useContext } from 'react';

import { getByPath } from '@/utils/widgets';
import { WorkflowContext } from '@/utils/workflowContext';

import { AvatarComponent } from '@/components/ui/avatar/avatar';
import { AvatarView } from '@/components/ui/widget/views/avatar/avatar.view.type';
import { WidgetData } from '@/components/ui/widget/widget.type';
import { TextView } from '@/components/ui/widget/views/text/text.view.type';
import { WorkflowContextType } from '@/types/workflow.types';

type Props = {
  view: AvatarView;
  data: WidgetData;
};

const AvatarViewImpl = memo(({ view, data }: Props) => {
  const workflowContext = useContext(WorkflowContext);

  const {
    path, // path to the name or label of user / group
    avatarPath, // path to the avatar of the user / group
    isGroup = false,
    useContext: useWorkflowContext = false // whether to consider path as pointing to a label and resolve name + avatar using the workflow context
  } = view;

  let name;
  let avatar;
  if (useWorkflowContext) {
    if (!workflowContext) {
      throw Error('No workflow context provided to the Avatar view');
    }

    if (isGroup) {
      const { groups = [] } = workflowContext;
      // path is pointing at the group label
      const label = getByPath(data, path);
      if (!label) return null;

      // find the group in the workflow
      const group = groups.find(g => g.label === label);

      if (!group) {
        // group not found, return the label only
        name = label;
      } else {
        ({ name, avatar } = group);
      }
    } else {
      throw Error('Contextualized user avatars not available yet');
    }
  } else {
    name = getByPath(data, path);
    avatar = getByPath(data, avatarPath);
  }

  if (!name && !avatar) return null;

  return (
    <div className="flex h-full flex-row flex-nowrap items-center justify-start gap-2 overflow-hidden">
      <span className="shrink-0">
        <AvatarComponent size={28} source={avatar} name={name} />
      </span>
      {name && <span className="truncate">{name}</span>}
    </div>
  );
});

// sort defaults to text on path
// potentially transformed through workflow context
const getSortConfig = (view: AvatarView): TextView => {
  const { path } = view;
  return {
    type: 'text',
    path
  };
};

// filtering defaults to text search on path
// potentially transformed through workflow context
const getFilterConfig = (view: AvatarView): TextView => {
  const { path } = view;
  return {
    type: 'text',
    path
  };
};

// width defaults to 'medium' if a label is shown, 'small' otherwise
const getDefaultWidth = () => 'medium' as const;

// stringifies data at namePath or path
const getStringifyFunction = (
  view: AvatarView,
  workflowContext: WorkflowContextType | undefined
) => {
  const {
    path,
    isGroup = false,
    useContext: useWorkflowContext = false
  } = view;

  if (useWorkflowContext) {
    if (!workflowContext) {
      throw Error('No workflow context provided to the Avatar view');
    }

    if (isGroup) {
      const { groups = [] } = workflowContext;

      return (data: WidgetData) => {
        // path is pointing at the group label
        const label = getByPath(data, path);
        if (!label) return null;

        // find the group in the workflow
        const group = groups.find(g => g.label === label);

        if (!group) {
          // group not found, return the label only
          return label;
        }
        return group.name;
      };
    }
    throw Error('Contextualized user avatars not available yet');
  } else {
    return data => getByPath(data, path);
  }
};

export const avatarViewProvider = {
  component: AvatarViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
